"use client";

import React, { useState } from 'react';
import { useMood } from '@/contexts/MoodContext';
import { Cloud, Haze, Sun } from 'lucide-react';

const moodConfig = {
  anxious: { label: 'Anxious', color: 'bg-warning-500', icon: Cloud },
  unclear: { label: 'Foggy', color: 'bg-authority-600', icon: Haze },
  clear: { label: 'Clear', color: 'bg-positive-600', icon: Sun },
} as const;

const TOOLTIP_DISMISSED_KEY = 'delphina_mood_tooltip_dismissed';

export default function MoodToggle() {
  const { mood, setMood } = useMood();
  const [tooltipDismissed, setTooltipDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(TOOLTIP_DISMISSED_KEY) === 'true';
    }
    return false;
  });

  const handleMoodChange = (newMood: keyof typeof moodConfig) => {
    if (!tooltipDismissed) {
      localStorage.setItem(TOOLTIP_DISMISSED_KEY, 'true');
      setTooltipDismissed(true);
    }
    setMood(newMood);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
      {mood !== 'clear' && !tooltipDismissed && (
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          <div className="relative bg-accessible-assets-100 border border-accessible-assets-200 shadow-sm text-accessible-assets-700 text-xs px-3 py-2 rounded-lg max-w-66 whitespace-normal">
            See more detailed data and forecasts. Switch to the full view when you are ready.
            <div className="absolute top-1/2 -right-2 transform -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-accessible-assets-200" />
          </div>
        </div>
      )}
      <div className="flex flex-row w-full items-center gap-1 bg-gray-100 rounded-lg sm:rounded-full p-1">
        {(Object.keys(moodConfig) as Array<keyof typeof moodConfig>).map((m) => {
          const config = moodConfig[m];
          const isActive = mood === m;
          const Icon = config.icon;


          return (
            <button
              key={m}
              onClick={() => handleMoodChange(m)}
              className={`flex-1 px-2 py-2 rounded-lg text-xs sm:text-sm font-bold sm:font-medium transition-all cursor-pointer flex items-center justify-center gap-1 ${
                isActive
                  ? `${config.color} text-white`
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
              <span>{config.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
